import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery } from "@rocicorp/zero/react";
import { appQueries } from "@production-hub/shared";
import { useMemo } from "react";
import { formatProjectName } from "@/lib/format-project-name";
import { useAllEquipment } from "@/hooks/useEquipment";
import { useBatchEquipmentInstances } from "@/hooks/useEquipmentInstances";
import { useProjects } from "@/hooks/useProjects";

export interface ConflictInstance {
  instanceId: string;
  identifier: string;
}

export interface EquipmentConflict {
  projectId: string;
  projectName: string;
  projectStart: string | null;
  projectEnd: string | null;
  groupName: string;
  amount: number;
  instances: ConflictInstance[];
  instanceIdentifiers: string[];
}

export interface ConflictSummary {
  equipmentId: string;
  equipmentName: string;
  isSerialized: boolean;
  totalUsed: number;
  stockQuantity: number | null;
  activeInstanceCount: number | null;
  conflicts: EquipmentConflict[];
  hasConflict: boolean;
  overlappingInstanceIds: string[];
}

interface UseEquipmentConflictsParams {
  projectId: string;
  startDate: string | null;
  endDate: string | null;
  equipmentIds: string[];
}

type ZeroAssignmentRow = {
  id: string;
  equipmentID?: string | null;
  amount: number;
  group?: {
    name?: string | null;
    projectID: string;
  } | null;
  instanceAssignments?: {
    instanceID: string;
    instance?: { identifier: string } | null;
  }[];
};

const EMPTY_UUID = "00000000-0000-0000-0000-000000000000";

function datesOverlap(
  projectStart: string | null,
  projectEnd: string | null,
  startDate: string,
  endDate: string,
) {
  if (!projectStart || !projectEnd) return false;
  return projectStart <= endDate && projectEnd >= startDate;
}

export function useEquipmentConflicts({
  projectId,
  startDate,
  endDate,
  equipmentIds,
}: UseEquipmentConflictsParams) {
  const enabled =
    !!projectId && !!startDate && !!endDate && equipmentIds.length > 0;
  const queryIds = enabled ? equipmentIds : [EMPTY_UUID];
  const [assignmentsData, details] = useZeroQuery(
    appQueries.equipmentConflicts.assignmentsByEquipment({
      equipmentIDs: queryIds,
    }),
    { enabled },
  );
  const { data: allEquipment = [] } = useAllEquipment();
  const { data: projects = [] } = useProjects(false);
  const { data: instancesByEquipment = {} } =
    useBatchEquipmentInstances(queryIds);

  const data = useMemo(() => {
    const conflictMap: Record<string, ConflictSummary> = {};
    if (!enabled || !startDate || !endDate) return conflictMap;

    const equipmentLookup = new Map(
      allEquipment
        .filter((equipment) => equipmentIds.includes(equipment.id))
        .map((equipment) => [equipment.id, equipment]),
    );
    const projectLookup = new Map(
      projects.map((project) => [project.id, project]),
    );
    const activeInstanceCounts = new Map<string, number>();

    for (const equipmentId of equipmentIds) {
      activeInstanceCounts.set(
        equipmentId,
        (instancesByEquipment[equipmentId] ?? []).filter(
          (instance) => instance.status === "active",
        ).length,
      );
    }

    const instanceUsageByEquipment = new Map<string, Map<string, string[]>>();

    for (const assignment of (assignmentsData ?? []) as ZeroAssignmentRow[]) {
      const equipmentId = assignment.equipmentID;
      if (!equipmentId || !equipmentIds.includes(equipmentId)) continue;

      const equipment = equipmentLookup.get(equipmentId);
      const group = assignment.group;
      const project = group?.projectID
        ? projectLookup.get(group.projectID)
        : undefined;
      if (!equipment || !project) continue;
      if (
        !datesOverlap(
          project.planning_start_date,
          project.planning_end_date,
          startDate,
          endDate,
        )
      ) {
        continue;
      }

      const instances =
        assignment.instanceAssignments?.map((instanceAssignment) => ({
          instanceId: instanceAssignment.instanceID,
          identifier: instanceAssignment.instance?.identifier ?? "",
        })) ?? [];

      if (equipment.is_serialized) {
        if (!instanceUsageByEquipment.has(equipmentId)) {
          instanceUsageByEquipment.set(equipmentId, new Map());
        }
        const instanceUsage = instanceUsageByEquipment.get(equipmentId)!;
        for (const instance of instances) {
          const projectIds = instanceUsage.get(instance.instanceId) ?? [];
          projectIds.push(project.id);
          instanceUsage.set(instance.instanceId, projectIds);
        }
      }

      let summary = conflictMap[equipmentId];
      if (!summary) {
        summary = {
          equipmentId,
          equipmentName: equipment.name,
          isSerialized: equipment.is_serialized,
          totalUsed: 0,
          stockQuantity: equipment.stock_quantity,
          activeInstanceCount: equipment.is_serialized
            ? (activeInstanceCounts.get(equipmentId) ?? 0)
            : null,
          conflicts: [],
          hasConflict: false,
          overlappingInstanceIds: [],
        };
        conflictMap[equipmentId] = summary;
      }

      summary.totalUsed += assignment.amount;
      summary.conflicts.push({
        projectId: project.id,
        projectName: formatProjectName(project.name, project.event_start_date),
        projectStart: project.planning_start_date,
        projectEnd: project.planning_end_date,
        groupName: group?.name ?? "",
        amount: assignment.amount,
        instances,
        instanceIdentifiers: instances.map((instance) => instance.identifier),
      });
    }

    for (const summary of Object.values(conflictMap)) {
      if (summary.isSerialized) {
        const instanceUsage = instanceUsageByEquipment.get(summary.equipmentId);
        const currentProjectConflict = summary.conflicts.find(
          (conflict) => conflict.projectId === projectId,
        );
        const currentInstanceCount =
          currentProjectConflict?.instanceIdentifiers.length || 0;

        if (currentInstanceCount > 0 && instanceUsage) {
          const overlappingIds: string[] = [];
          for (const [instanceId, projectIds] of instanceUsage.entries()) {
            if (
              projectIds.includes(projectId) &&
              projectIds.some((id) => id !== projectId)
            ) {
              overlappingIds.push(instanceId);
            }
          }
          summary.overlappingInstanceIds = overlappingIds;
          summary.hasConflict = overlappingIds.length > 0;
        } else if (summary.activeInstanceCount !== null) {
          summary.hasConflict = summary.totalUsed > summary.activeInstanceCount;
        }
      } else if (summary.stockQuantity !== null) {
        summary.hasConflict = summary.totalUsed > summary.stockQuantity;
      }
    }

    return conflictMap;
  }, [
    allEquipment,
    assignmentsData,
    enabled,
    endDate,
    equipmentIds,
    instancesByEquipment,
    projectId,
    projects,
    startDate,
  ]);

  return {
    data,
    isLoading: enabled && details.type !== "complete",
    isFetching: enabled && details.type !== "complete",
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}
